// Captures a real still frame from a video file client-side (canvas), so the
// gallery grid can show an instant JPEG thumbnail instead of having to load
// into the video file itself just to preview it.
//
// Hard-capped at 8s: for some video files/codecs the <video> element's
// "seeked" event can simply never fire (observed in practice), and without a
// timeout that left this Promise pending forever -- silently blocking the
// whole upload at the final step even though every chunk had already made it
// to storage, since the caller awaits this before marking the file done.
const POSTER_TIMEOUT_MS = 8_000;

export function captureVideoPoster(file) {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    const url = URL.createObjectURL(file);
    video.src = url;

    let settled = false;
    const timeoutId = setTimeout(() => finish(() => reject(new Error("poster capture timed out"))), POSTER_TIMEOUT_MS);

    const cleanup = () => {
      clearTimeout(timeoutId);
      URL.revokeObjectURL(url);
      video.removeAttribute("src");
      video.load();
    };
    const finish = (action) => {
      if (settled) return;
      settled = true;
      cleanup();
      action();
    };

    video.addEventListener("loadeddata", () => {
      // A hair past the very start avoids a black/transparent first frame on
      // some codecs.
      video.currentTime = Math.min(0.5, (video.duration || 1) / 10);
    });
    video.addEventListener("seeked", () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth || 480;
        canvas.height = video.videoHeight || 480;
        canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            if (!blob) return finish(() => reject(new Error("poster capture failed")));
            finish(() => resolve(new File([blob], "poster.jpg", { type: "image/jpeg" })));
          },
          "image/jpeg",
          0.8,
        );
      } catch (err) {
        finish(() => reject(err));
      }
    });
    video.addEventListener("error", () => finish(() => reject(new Error("video load failed"))));
  });
}
